import { 
  doc,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  onSnapshot,
  deleteDoc,
  runTransaction
} from 'firebase/firestore';
import { db } from './config';
import { getCardById } from '../data/cards';

// Generate a random 4-character lobby code
export const generateLobbyCode = () => {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Omitting confusing characters
  let result = '';
  for (let i = 0; i < 4; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

// Create a new lobby
export const createLobby = async (hostName) => {
  const lobbyCode = generateLobbyCode();
  const lobbyRef = doc(db, 'lobbies', lobbyCode);

  // Check if lobby code already exists (very unlikely but possible)
  const docSnap = await getDoc(lobbyRef);
  if (docSnap.exists()) {
    // If exists, try again with a new code
    return createLobby(hostName);
  }

  // Create initial lobby data
  const lobbyData = {
    code: lobbyCode,
    hostId: null, // Will be updated after player is created
    gameStarted: false,
    players: [],
    settings: {
      discussionTimeMinutes: 3,
      selectedCards: []
    },
    centerCards: [],
    turnManager: {
      currentTurn: null,
      turnOrder: [],
      completedTurns: []
    },
    votes: {},
    gameResults: null,
    eliminatedPlayerIds: [],
    roundNumber: 0,
    roundResult: null,
    createdAt: new Date()
  };

  // Create the lobby
  await setDoc(lobbyRef, lobbyData);

  // Add host as first player
  const playerId = await addPlayerToLobby(lobbyCode, hostName);
  
  // Update host ID in lobby data
  await updateDoc(lobbyRef, {
    hostId: playerId
  });

  return { lobbyCode, playerId };
};

// Add player to a lobby
export const addPlayerToLobby = async (lobbyCode, playerName) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobi bulunamadı');
  }
  
  if (lobbyDoc.data().gameStarted) {
    throw new Error('Oyun zaten başlamış');
  }

  // Check if name is unique in this lobby
  const existingPlayers = lobbyDoc.data().players || [];
  if (existingPlayers.some(p => p.name === playerName)) {
    throw new Error('Bu isimde bir oyuncu zaten var');
  }

  // Create player object
  const playerId = Date.now().toString();
  const playerData = {
    id: playerId,
    name: playerName,
    card: null,
    originalCard: null,
    isSilenced: false,
    isReady: false,
    joinedAt: new Date()
  };

  // Add player to lobby
  await updateDoc(lobbyRef, {
    players: arrayUnion(playerData)
  });

  return playerId;
};

// Get a lobby by code (one-time fetch)
export const getLobby = async (lobbyCode) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobi bulunamadı');
  }
  
  return { id: lobbyDoc.id, ...lobbyDoc.data() };
};

// Listen to lobby changes (real-time)
export const subscribeLobby = (lobbyCode, callback) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  return onSnapshot(lobbyRef, (doc) => {
    if (doc.exists()) {
      callback({ id: doc.id, ...doc.data() });
    } else {
      callback(null);
    }
  });
};

// Update player data
export const updatePlayer = async (lobbyCode, playerId, playerData) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobi bulunamadı');
  }
  
  const players = lobbyDoc.data().players;
  const playerIndex = players.findIndex(p => p.id === playerId);
  
  if (playerIndex === -1) {
    throw new Error('Oyuncu bulunamadı');
  }
  
  const updatedPlayers = [...players];
  updatedPlayers[playerIndex] = { 
    ...updatedPlayers[playerIndex], 
    ...playerData 
  };
  
  await updateDoc(lobbyRef, {
    players: updatedPlayers
  });
};

// Update lobby settings
export const updateLobbySettings = async (lobbyCode, settings) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  await updateDoc(lobbyRef, {
    settings: settings
  });
};

// Start the game
export const startGame = async (lobbyCode, selectedCardIds) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobi bulunamadı');
  }
  
  const players = lobbyDoc.data().players;
  
  // We need at least 3 players to start
  if (players.length < 3) {
    throw new Error('Oyun başlatmak için en az 3 oyuncu gerekli');
  }
  
  // Check if we have enough selected cards
  // (# of players + 3 center cards)
  const neededCards = players.length + 3;
  if (selectedCardIds.length !== neededCards) {
    throw new Error(`Oyun başlatmak için ${neededCards} kart seçilmeli`);
  }
  
  // Shuffle cards with balance validation
  const maxAttempts = 100;
  let playerCards, centerCards;
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const shuffled = [...selectedCardIds].sort(() => Math.random() - 0.5);
    playerCards = shuffled.slice(0, players.length);
    centerCards = shuffled.slice(players.length);
    
    const wolfCount = playerCards.filter(id => {
      const c = getCardById(id);
      return c && c.team === 'werewolf';
    }).length;
    const villagerCount = playerCards.filter(id => {
      const c = getCardById(id);
      return c && c.team === 'villager';
    }).length;
    
    if (wolfCount >= 1 && wolfCount < villagerCount) break;
  }
  
  // Update player data with cards
  const updatedPlayers = players.map((player, index) => ({
    ...player,
    card: playerCards[index],
    originalCard: playerCards[index]
  }));
  
  // Build turn order based on card turnOrder values (skip turnOrder=0 which means no night action)
  const playersWithTurnOrder = updatedPlayers
    .map(player => {
      const card = getCardById(player.card);
      return { playerId: player.id, turnOrder: card ? card.turnOrder : 0 };
    })
    .filter(p => p.turnOrder > 0)
    .sort((a, b) => a.turnOrder - b.turnOrder);

  const sortedTurnOrder = playersWithTurnOrder.map(p => p.playerId);
  
  // Start with CARD_REVEAL phase so players can see their cards first
  await updateDoc(lobbyRef, {
    gameStarted: true,
    players: updatedPlayers,
    centerCards: centerCards,
    readyPlayers: [],
    turnManager: {
      currentTurn: 'CARD_REVEAL',
      turnOrder: sortedTurnOrder,
      completedTurns: []
    }
  });
};

// Mark a player as ready (after viewing their card)
export const markPlayerReady = async (lobbyCode, playerId) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  
  await runTransaction(db, async (transaction) => {
    const lobbyDoc = await transaction.get(lobbyRef);
    
    if (!lobbyDoc.exists()) {
      throw new Error('Lobi bulunamadı');
    }
    
    const lobbyData = lobbyDoc.data();
    const readyPlayers = lobbyData.readyPlayers || [];
    
    if (readyPlayers.includes(playerId)) return;
    
    const updatedReady = [...readyPlayers, playerId];
    const allReady = updatedReady.length >= lobbyData.players.length;
    
    const updates = { readyPlayers: updatedReady };
    
    if (allReady) {
      const firstTurn = lobbyData.turnManager.turnOrder.length > 0
        ? lobbyData.turnManager.turnOrder[0]
        : 'DISCUSSION';
      updates['turnManager.currentTurn'] = firstTurn;
    }
    
    transaction.update(lobbyRef, updates);
  });
};

// Record a card action result and apply side effects to game state
export const recordCardAction = async (lobbyCode, action, shouldProcessNextTurn = false) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobi bulunamadı');
  }

  const lobbyData = lobbyDoc.data();
  const updates = {
    'turnManager.completedTurns': arrayUnion(action)
  };

  // Apply side effects based on action type
  switch (action.action) {
    case 'COPY_PLAYER_CARD': {
      // Loki: copy target player's card
      const players = [...lobbyData.players];
      const actorIdx = players.findIndex(p => p.id === action.playerId);
      const targetIdx = players.findIndex(p => p.id === action.targetPlayerId);
      if (actorIdx !== -1 && targetIdx !== -1) {
        players[actorIdx] = { ...players[actorIdx], card: players[targetIdx].card };
        updates.players = players;
      }
      break;
    }
    case 'STEAL_CARD': {
      // Hermes: swap cards with target player
      const players = [...lobbyData.players];
      const actorIdx = players.findIndex(p => p.id === action.playerId);
      const targetIdx = players.findIndex(p => p.id === action.targetPlayerId);
      if (actorIdx !== -1 && targetIdx !== -1) {
        const actorCard = players[actorIdx].card;
        const targetCard = players[targetIdx].card;
        players[actorIdx] = { ...players[actorIdx], card: targetCard };
        players[targetIdx] = { ...players[targetIdx], card: actorCard };
        updates.players = players;
      }
      break;
    }
    case 'SWAP_PLAYER_CARDS': {
      // Aphrodite: swap two other players' cards
      const players = [...lobbyData.players];
      const [id1, id2] = action.targetPlayerIds;
      const idx1 = players.findIndex(p => p.id === id1);
      const idx2 = players.findIndex(p => p.id === id2);
      if (idx1 !== -1 && idx2 !== -1) {
        const card1 = players[idx1].card;
        const card2 = players[idx2].card;
        players[idx1] = { ...players[idx1], card: card2 };
        players[idx2] = { ...players[idx2], card: card1 };
        updates.players = players;
      }
      break;
    }
    case 'RANDOM_CENTER_CARD': {
      // Dionysos: swap own card with a random center card
      const players = [...lobbyData.players];
      const centerCards = [...lobbyData.centerCards];
      const actorIdx = players.findIndex(p => p.id === action.playerId);
      const centerIdx = action.centerCardIndex;
      if (actorIdx !== -1 && centerIdx >= 0 && centerIdx < centerCards.length) {
        const actorCard = players[actorIdx].card;
        players[actorIdx] = { ...players[actorIdx], card: centerCards[centerIdx] };
        centerCards[centerIdx] = actorCard;
        updates.players = players;
        updates.centerCards = centerCards;
      }
      break;
    }
    case 'VIEW_TWO_CARDS_CHANGE_ROLE': {
      // Gareth: view two players' cards, become werewolf/tanner if found
      if (action.changeToCard) {
        const players = [...lobbyData.players];
        const actorIdx = players.findIndex(p => p.id === action.playerId);
        if (actorIdx !== -1) {
          players[actorIdx] = { ...players[actorIdx], card: action.changeToCard };
          updates.players = players;
        }
      }
      break;
    }
    case 'SILENCE_PLAYER': {
      // Eris: silence a player for discussion phase
      const players = [...lobbyData.players];
      const targetIdx = players.findIndex(p => p.id === action.targetPlayerId);
      if (targetIdx !== -1) {
        players[targetIdx] = { ...players[targetIdx], isSilenced: true };
        updates.players = players;
      }
      break;
    }
    default:
      break;
  }

  await updateDoc(lobbyRef, updates);
  
  if (shouldProcessNextTurn) {
    await processNextTurn(lobbyCode);
  }
};

// Process next turn
export const processNextTurn = async (lobbyCode) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobi bulunamadı');
  }
  
  const { turnManager } = lobbyDoc.data();
  const { turnOrder, completedTurns } = turnManager;
  
  const isNightOver = completedTurns.length >= turnOrder.length;
  
  if (!isNightOver) {
    const nextPlayer = turnOrder.find(playerId => 
      !completedTurns.some(action => action.playerId === playerId)
    );
    
    if (nextPlayer) {
      await updateDoc(lobbyRef, {
        'turnManager.currentTurn': nextPlayer
      });
      return;
    }
  }
  
  // Night is over — check for Zeus reveal before starting discussion
  const updates = { 'turnManager.currentTurn': 'DISCUSSION' };
  
  const zeusAction = completedTurns.find(a => a.action === 'REVEAL_AT_END');
  if (zeusAction && zeusAction.targetPlayerId) {
    const players = lobbyDoc.data().players;
    const revealedPlayer = players.find(p => p.id === zeusAction.targetPlayerId);
    if (revealedPlayer) {
      updates.zeusReveal = {
        playerId: revealedPlayer.id,
        playerName: revealedPlayer.name,
        cardId: revealedPlayer.card
      };
    }
  }
  
  await updateDoc(lobbyRef, updates);
};

const getPlayerTeam = (player) => {
  const card = getCardById(player.card);
  return card ? card.team : 'villager';
};

// Process a round's voting result and check win conditions
export const processRoundResult = async (lobbyCode) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobi bulunamadı');
  }
  
  const data = lobbyDoc.data();
  const { players, votes, turnManager } = data;
  const completedTurns = turnManager?.completedTurns || [];
  const previouslyEliminated = data.eliminatedPlayerIds || [];
  const currentRound = (data.roundNumber || 0) + 1;
  
  const alivePlayers = players.filter(p => !previouslyEliminated.includes(p.id));
  
  // Count votes from alive players only
  const voteCounts = {};
  alivePlayers.forEach(p => { voteCounts[p.id] = 0; });
  
  Object.entries(votes || {}).forEach(([voterId, targetId]) => {
    if (!previouslyEliminated.includes(voterId) && voteCounts[targetId] !== undefined) {
      voteCounts[targetId]++;
    }
  });
  
  // Find player(s) with most votes
  let maxVotes = 0;
  let votedOutCandidates = [];
  
  Object.entries(voteCounts).forEach(([pid, count]) => {
    if (count > maxVotes) {
      maxVotes = count;
      votedOutCandidates = [pid];
    } else if (count === maxVotes && count > 0) {
      votedOutCandidates.push(pid);
    }
  });
  
  // Tie: no one eliminated by vote
  let newlyEliminated = [];
  let isTie = votedOutCandidates.length !== 1;
  
  if (votedOutCandidates.length === 1) {
    newlyEliminated.push(votedOutCandidates[0]);
  }
  
  // Thor ability: alive player with thor card — their vote target dies
  let thorKillId = null;
  const thorPlayer = alivePlayers.find(p => p.card === 'thor');
  if (thorPlayer && votes[thorPlayer.id]) {
    const thorTarget = votes[thorPlayer.id];
    if (!previouslyEliminated.includes(thorTarget)) {
      thorKillId = thorTarget;
      if (!newlyEliminated.includes(thorTarget)) {
        newlyEliminated.push(thorTarget);
      }
    }
  }
  
  // Artemis ability: if Artemis is newly eliminated, her vote target also dies
  let artemisKillId = null;
  const artemisVictim = newlyEliminated.find(pid => {
    const p = players.find(pl => pl.id === pid);
    return p && p.card === 'artemis';
  });
  if (artemisVictim && votes[artemisVictim]) {
    const artemisTarget = votes[artemisVictim];
    if (!previouslyEliminated.includes(artemisTarget) && !newlyEliminated.includes(artemisTarget)) {
      artemisKillId = artemisTarget;
      newlyEliminated.push(artemisTarget);
    }
  }
  
  const allEliminatedIds = [...previouslyEliminated, ...newlyEliminated];
  
  // Determine alive players after this round
  const aliveAfter = players.filter(p => !allEliminatedIds.includes(p.id));
  const aliveWolves = aliveAfter.filter(p => getPlayerTeam(p) === 'werewolf');
  const aliveVillagers = aliveAfter.filter(p => getPlayerTeam(p) === 'villager');
  
  // Check win conditions (priority order)
  let isGameOver = false;
  let winningTeam = null;
  let winMessage = null;
  
  // 1. Tanner eliminated this round → tanner wins
  const tannerEliminated = newlyEliminated.find(pid => {
    const p = players.find(pl => pl.id === pid);
    return p && getPlayerTeam(p) === 'tanner';
  });
  
  if (tannerEliminated) {
    const tannerPlayer = players.find(p => p.id === tannerEliminated);
    isGameOver = true;
    winningTeam = 'tanner';
    winMessage = `Düzenbaz (${tannerPlayer.name}) köyden atıldı ve amacına ulaştı! Düzenbaz kazandı!`;
  }
  // 2. No wolves alive → villagers win
  else if (aliveWolves.length === 0) {
    isGameOver = true;
    winningTeam = 'villager';
    winMessage = 'Tüm kurtlar elendi! Köylü takımı kazandı!';
  }
  // 3. Wolves >= villagers → wolves win
  else if (aliveWolves.length >= aliveVillagers.length) {
    isGameOver = true;
    winningTeam = 'werewolf';
    winMessage = 'Kurtlar köylülere eşit veya daha fazla sayıya ulaştı! Kurt takımı kazandı!';
  }
  
  // Build eliminated player details for display
  const eliminatedDetails = newlyEliminated.map(pid => {
    const p = players.find(pl => pl.id === pid);
    return p ? { id: p.id, name: p.name, card: p.card } : null;
  }).filter(Boolean);
  
  const roundResult = {
    roundNumber: currentRound,
    eliminatedThisRound: eliminatedDetails,
    isTie,
    thorKillId,
    artemisKillId,
    isGameOver,
    winningTeam,
    winMessage,
    maxVotes,
    votes
  };
  
  const updates = {
    roundNumber: currentRound,
    eliminatedPlayerIds: allEliminatedIds,
    roundResult,
    'turnManager.currentTurn': 'ROUND_RESULT'
  };
  
  if (isGameOver) {
    updates.gameResults = {
      winningTeam,
      winMessage,
      allEliminatedIds,
      finalCards: players.map(p => ({
        playerId: p.id,
        playerName: p.name,
        cardId: p.card,
        originalCardId: p.originalCard || p.card,
        isEliminated: allEliminatedIds.includes(p.id)
      }))
    };
  }
  
  await updateDoc(lobbyRef, updates);
  return roundResult;
};

// Continue to the next discussion round
export const continueToNextRound = async (lobbyCode) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobi bulunamadı');
  }
  
  // Clear silence (Eris only lasts first round)
  const players = lobbyDoc.data().players.map(p => ({ ...p, isSilenced: false }));
  
  await updateDoc(lobbyRef, {
    'turnManager.currentTurn': 'DISCUSSION',
    votes: {},
    roundResult: null,
    discussionStartedAt: null,
    discussionReadyPlayers: [],
    players
  });
};

// Transition from ROUND_RESULT to final GAME_OVER screen
export const showFinalResults = async (lobbyCode) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  await updateDoc(lobbyRef, {
    'turnManager.currentTurn': 'GAME_OVER'
  });
};

// Clean up old lobbies (could be called by a Cloud Function)
export const cleanupOldLobbies = async () => {
  // Logic to delete lobbies older than 24 hours
  // This would typically be implemented as a Cloud Function
};

// Start the discussion timer (synced across all clients)
export const startDiscussion = async (lobbyCode) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  await updateDoc(lobbyRef, {
    discussionStartedAt: new Date()
  });
};

// Mark a player as ready to end discussion
export const markDiscussionReady = async (lobbyCode, playerId) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  
  await runTransaction(db, async (transaction) => {
    const lobbyDoc = await transaction.get(lobbyRef);
    
    if (!lobbyDoc.exists()) {
      throw new Error('Lobi bulunamadı');
    }
    
    const data = lobbyDoc.data();
    const discussionReadyPlayers = [...(data.discussionReadyPlayers || [])];
    const eliminatedIds = data.eliminatedPlayerIds || [];
    
    if (discussionReadyPlayers.includes(playerId)) return;
    discussionReadyPlayers.push(playerId);
    
    const alivePlayers = data.players.filter(p => !eliminatedIds.includes(p.id));
    
    if (discussionReadyPlayers.length >= alivePlayers.length) {
      transaction.update(lobbyRef, {
        'turnManager.currentTurn': 'VOTING',
        discussionStartedAt: null,
        discussionReadyPlayers: [],
        votes: {}
      });
    } else {
      transaction.update(lobbyRef, {
        discussionReadyPlayers
      });
    }
  });
};

// Move from discussion phase to voting phase
export const moveToVotingPhase = async (lobbyCode) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  
  await updateDoc(lobbyRef, {
    'turnManager.currentTurn': 'VOTING',
    discussionStartedAt: null,
    discussionReadyPlayers: [],
    votes: {}
  });
};

// Submit a player's vote
export const submitVote = async (lobbyCode, playerId, targetPlayerId) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  
  await updateDoc(lobbyRef, {
    [`votes.${playerId}`]: targetPlayerId
  });
};

// Finalize votes and process round result
export const finalizeVotes = async (lobbyCode) => {
  return processRoundResult(lobbyCode);
};

// Reset the game for another round
export const resetGame = async (lobbyCode) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    throw new Error('Lobi bulunamadı');
  }
  
  // Reset the game state but keep players
  await updateDoc(lobbyRef, {
    gameStarted: false,
    centerCards: [],
    'turnManager.currentTurn': null,
    'turnManager.turnOrder': [],
    'turnManager.completedTurns': [],
    votes: {},
    gameResults: null,
    eliminatedPlayerIds: [],
    roundNumber: 0,
    roundResult: null,
    discussionStartedAt: null,
    discussionReadyPlayers: [],
    readyPlayers: [],
    zeusReveal: null,
    players: lobbyDoc.data().players.map(player => ({
      ...player,
      card: null,
      originalCard: null,
      isSilenced: false,
      isReady: false
    }))
  });
};

// Leave the lobby
export const leaveLobby = async (lobbyCode, playerId) => {
  const lobbyRef = doc(db, 'lobbies', lobbyCode);
  const lobbyDoc = await getDoc(lobbyRef);
  
  if (!lobbyDoc.exists()) {
    return; // Lobby doesn't exist, nothing to do
  }
  
  const players = lobbyDoc.data().players || [];
  const updatedPlayers = players.filter(p => p.id !== playerId);
  
  // If this is the last player, delete the lobby
  if (updatedPlayers.length === 0) {
    await deleteDoc(lobbyRef);
    return;
  }
  
  // Otherwise update the lobby with the remaining players
  const updates = { players: updatedPlayers };
  
  // If the leaving player was the host, assign a new host
  if (lobbyDoc.data().hostId === playerId) {
    updates.hostId = updatedPlayers[0].id;
  }
  
  await updateDoc(lobbyRef, updates);
};
